import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Task, TaskInput, TaskStatus } from '@underline/shared';
import { api } from '@/lib/api';

interface ListParams {
  responsible_id?: number;
  status?: TaskStatus;
  from?: string;
  to?: string;
  brand_id?: number;
}

export function useTasks(params: ListParams = {}) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () =>
      api
        .get('tasks', {
          searchParams: Object.fromEntries(
            Object.entries(params).filter(([, v]) => v != null),
          ) as Record<string, string | number>,
        })
        .json<Task[]>(),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TaskInput) => api.post('tasks', { json: input }).json<Task>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useToggleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`tasks/${id}/toggle`).json<Task>(),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      await qc.cancelQueries({ queryKey: ['events'] });

      const previousTasks = qc.getQueriesData<Task[]>({ queryKey: ['tasks'] });
      const previousEvents = qc.getQueriesData({ queryKey: ['events'] });

      qc.setQueriesData<Task[]>({ queryKey: ['tasks'] }, (old) =>
        old?.map((t) =>
          t.id === id
            ? { ...t, status: t.status === 'done' ? 'pending' : 'done' }
            : t,
        ) ?? old,
      );

      return { previousTasks, previousEvents };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      ctx.previousTasks.forEach(([key, data]) => qc.setQueryData(key, data));
      ctx.previousEvents.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<TaskInput> }) =>
      api.patch(`tasks/${id}`, { json: input }).json<Task>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`tasks/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}
